import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, MagnifyingGlassIcon, LinkIcon } from '@heroicons/react/24/outline';
import { Input } from '@/components/ui/Input';
import { incidentsService } from '@/services/incidents.service';
import { investigationsService } from '@/services/investigations.service';

interface LinkSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'incident' | 'investigation';
  excludeIds: string[];
  onSelect: (id: string) => Promise<void>;
}

interface SearchResult {
  id: string;
  number: string;
  title: string;
  status: string;
}

const STATUS_COLORS: Record<string, string> = {
  OPEN: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  IN_PROGRESS: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  ACTIVE: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  RESOLVED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  CLOSED: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-900/30 dark:text-secondary-300',
  PENDING: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  SUSPENDED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  CANCELLED: 'bg-secondary-100 text-secondary-600 dark:bg-secondary-900/30 dark:text-secondary-400',
};

export default function LinkSearchModal({
  isOpen,
  onClose,
  type,
  excludeIds,
  onSelect,
}: LinkSearchModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLinking, setIsLinking] = useState(false);

  const label = type === 'incident' ? 'Incident' : 'Investigation';

  useEffect(() => {
    const search = async () => {
      if (searchQuery.trim().length < 2) {
        setResults([]);
        return;
      }

      try {
        setIsSearching(true);

        if (type === 'incident') {
          const response = await incidentsService.getIncidents({
            search: searchQuery,
            limit: 10,
            page: 1,
          });
          const items = (response.incidents || [])
            .filter((i: { id: string }) => !excludeIds.includes(i.id))
            .map((i: { id: string; incidentNumber: string; title: string; status: string }) => ({
              id: i.id,
              number: i.incidentNumber,
              title: i.title,
              status: i.status,
            }));
          setResults(items);
        } else {
          const response = await investigationsService.getInvestigations({
            search: searchQuery,
            limit: 10,
            page: 1,
          });
          const items = (response.investigations || [])
            .filter((i: { id: string }) => !excludeIds.includes(i.id))
            .map((i: { id: string; caseNumber: string; title: string; status: string }) => ({
              id: i.id,
              number: i.caseNumber,
              title: i.title,
              status: i.status,
            }));
          setResults(items);
        }
      } catch (error) {
        console.error(`Failed to search ${type}s:`, error);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(search, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, type, excludeIds]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setResults([]);
      setIsLinking(false);
    }
  }, [isOpen]);

  const handleSelect = async (item: SearchResult) => {
    try {
      setIsLinking(true);
      await onSelect(item.id);
      onClose();
    } catch (error) {
      console.error(`Failed to link ${type}:`, error);
    } finally {
      setIsLinking(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          <div className="fixed inset-0 flex items-start justify-center z-50 p-4 pt-16 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl w-full max-w-md pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-secondary-200 dark:border-secondary-700">
                <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
                  Link {label}
                </h2>
                <button
                  onClick={onClose}
                  className="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
                  <Input
                    type="text"
                    placeholder={`Search ${type}s by title or number...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    autoFocus
                  />
                </div>

                <div className="max-h-64 overflow-y-auto space-y-2">
                  {isSearching && (
                    <div className="text-center py-8 text-secondary-500 dark:text-secondary-400">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
                      <p className="mt-2 text-sm">Searching...</p>
                    </div>
                  )}

                  {!isSearching && searchQuery.trim().length > 0 && results.length === 0 && (
                    <div className="text-center py-8 text-secondary-500 dark:text-secondary-400">
                      <LinkIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No {type}s found</p>
                    </div>
                  )}

                  {!isSearching && searchQuery.trim().length < 2 && (
                    <div className="text-center py-8 text-secondary-500 dark:text-secondary-400">
                      <MagnifyingGlassIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Type at least 2 characters to search</p>
                    </div>
                  )}

                  {results.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleSelect(item)}
                      disabled={isLinking}
                      className="w-full text-left p-3 rounded-lg border-2 border-secondary-200 dark:border-secondary-700 hover:border-primary-500 dark:hover:border-primary-500 transition-all disabled:opacity-50"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-mono text-secondary-500 dark:text-secondary-400">
                              {item.number}
                            </span>
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                STATUS_COLORS[item.status] ?? STATUS_COLORS.OPEN
                              }`}
                            >
                              {item.status}
                            </span>
                          </div>
                          <p className="font-medium text-secondary-900 dark:text-white truncate">
                            {item.title}
                          </p>
                        </div>
                        <LinkIcon className="w-4 h-4 text-secondary-400 flex-shrink-0 ml-2" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end p-6 border-t border-secondary-200 dark:border-secondary-700">
                <button
                  onClick={onClose}
                  disabled={isLinking}
                  className="px-4 py-2 text-sm font-medium text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
